@if (message().role === 'user') {
	<!-- User text is shown verbatim (never markdown-rendered) — intent + safety. -->
	<div class="cs-chat__bubble">{{ message().content }}</div>
} @else {
	<div class="cs-chat__bubble">
		@for (seg of bodySegments(); track $index) {
			@if (seg.kind === 'markdown') {
				<cs-markdown [text]="seg.text || ''" [streaming]="message().streaming === true"></cs-markdown>
			} @else if (seg.kind === 'chart') {
				@if (seg.spec && seg.spec.type === 'card') {
					<!-- KPI cards render as HTML, not SVG: typography reflows, themes, copies
					     as text, and exports to PDF without rasterizing. -->
					<figure class="cs-chat__chart cs-chat__cards">
						@if (seg.spec.title) {
							<figcaption class="cs-chat__chart-title">{{ seg.spec.title }}</figcaption>
						}
						@for (group of cardGroups(seg.spec); track $index) {
							@if (group.header) {
								<div class="cs-chat__card-group">{{ group.header }}</div>
							}
							<div class="cs-chat__card-row">
								@for (card of group.cards; track $index) {
									<div class="cs-chat__card" [title]="card.fullValue">
										<div class="cs-chat__card-value">{{ card.value }}</div>
										<div class="cs-chat__card-label">{{ card.label }}</div>
									</div>
								}
							</div>
						}
					</figure>
				} @else if (seg.spec) {
					<figure class="cs-chat__chart">
						<div class="cs-chat__chart-actions">
							<button class="cs-chat__chart-action"
									type="button"
									(click)="openLightbox(seg.spec)"
									aria-label="View chart full size"
									title="View chart full size">⤢ expand</button>
							<button class="cs-chat__chart-action cs-chat__chart-copy"
									type="button"
									(click)="copyChart($event, $index)"
									[attr.aria-label]="'Copy chart as image'"
									title="Copy chart as image">
								{{ copiedChart === $index ? '✓ copied' : '⧉ copy' }}
							</button>
						</div>
						@if (seg.spec.title) {
							<figcaption class="cs-chat__chart-title">{{ seg.spec.title }}</figcaption>
						}
						<cs-chart [type]="seg.spec.type"
								  [data]="seg.spec.data"
								  [height]="chartHeightFor(seg.spec)"
								  [options]="chartOptions"></cs-chart>
					</figure>
				}
			} @else if (seg.kind === 'chart-pending') {
				<!-- Cohelion mark as the loader: the S-curve breathes, the dot blinks in counter-phase. -->
				<div class="cs-chat__chart-pending" aria-live="polite">
					<svg class="cs-chat__chart-pending-logo" viewBox="0 0 40 63" aria-hidden="true">
						<path class="cs-chat__chart-pending-curve" fill="#007EFF"
							  d="M39.9,12.9c0,7-5.7,12.8-12.8,12.9c-3.5,0-6.3,1.3-8.2,4.3c-2.1,3.4-2.1,6.8,0.1,10.1c1.8,2.8,4.3,4.6,7.7,4.8 c0.8,0.1,1.6,0,2.4-0.1c2.9-0.4,5.5,0.1,7.7,2c2.8,2.5,3.8,5.7,2.7,9.3s-3.7,5.7-7.4,6.2c-4.6,0.6-8.8-2.4-9.8-6.9 c-0.5-2.3-1.5-4.3-3.3-5.8c-2.3-1.9-4.9-2.5-7.9-2c-2.5,0.3-4.9,0.1-7-1.3c-2-1.4-3.3-3.2-3.8-5.6c-1-4.5,1.7-9.1,6.1-10.4 c0.8-0.2,1.5-0.3,2.3-0.4c1.6-0.2,2.9-0.9,3.9-2c1.9-2,3-4.4,2.9-7.3c0-1.1-0.2-2.1-0.6-3.1c-3-7.7,1.8-16.1,9.9-17.4 C32-1,38.7,3.9,39.7,11.1C39.8,11.7,39.8,12.3,39.9,12.9z"/>
						<path class="cs-chat__chart-pending-dot" fill="currentColor"
							  d="M11.3,20.7c0,3.1-2.6,5.6-5.7,5.6S0,23.7,0,20.7C0,17.6,2.6,15,5.7,15S11.3,17.6,11.3,20.7z"/>
					</svg>
					<span>building chart…</span>
				</div>
			} @else if (seg.kind === 'diagram') {
				<!-- Process/flow diagram: auto-laid-out SVG boxes + connectors with hover tooltips. -->
				@if (seg.diagram) {
					<cs-process-diagram [spec]="seg.diagram"></cs-process-diagram>
				}
			} @else if (seg.kind === 'diagram-pending') {
				<div class="cs-chat__chart-pending" aria-live="polite">
					<svg class="cs-chat__chart-pending-logo" viewBox="0 0 40 63" aria-hidden="true">
						<path class="cs-chat__chart-pending-curve" fill="#007EFF"
							  d="M39.9,12.9c0,7-5.7,12.8-12.8,12.9c-3.5,0-6.3,1.3-8.2,4.3c-2.1,3.4-2.1,6.8,0.1,10.1c1.8,2.8,4.3,4.6,7.7,4.8 c0.8,0.1,1.6,0,2.4-0.1c2.9-0.4,5.5,0.1,7.7,2c2.8,2.5,3.8,5.7,2.7,9.3s-3.7,5.7-7.4,6.2c-4.6,0.6-8.8-2.4-9.8-6.9 c-0.5-2.3-1.5-4.3-3.3-5.8c-2.3-1.9-4.9-2.5-7.9-2c-2.5,0.3-4.9,0.1-7-1.3c-2-1.4-3.3-3.2-3.8-5.6c-1-4.5,1.7-9.1,6.1-10.4 c0.8-0.2,1.5-0.3,2.3-0.4c1.6-0.2,2.9-0.9,3.9-2c1.9-2,3-4.4,2.9-7.3c0-1.1-0.2-2.1-0.6-3.1c-3-7.7,1.8-16.1,9.9-17.4 C32-1,38.7,3.9,39.7,11.1C39.8,11.7,39.8,12.3,39.9,12.9z"/>
						<path class="cs-chat__chart-pending-dot" fill="currentColor"
							  d="M11.3,20.7c0,3.1-2.6,5.6-5.7,5.6S0,23.7,0,20.7C0,17.6,2.6,15,5.7,15S11.3,17.6,11.3,20.7z"/>
					</svg>
					<span>building diagram…</span>
				</div>
			} @else if (seg.kind === 'annotated-image') {
				<!-- Documentation screenshot with read-only annotation overlays (from ShowDocImage). -->
				@if (seg.image) {
					<cs-annotated-image [spec]="seg.image"></cs-annotated-image>
				}
			} @else if (seg.kind === 'artifact') {
				<!-- Downloadable file produced via the ExportArtifact tool — persists with the transcript. -->
				<a class="cs-chat__artifact-chip"
				   [href]="artifactHref(seg.artifact?.id)"
				   [download]="seg.artifact?.name"
				   [title]="'Download ' + seg.artifact?.name">
					<span class="cs-chat__artifact-icon" aria-hidden="true">⤓</span>
					{{ seg.artifact?.name }}
					@if (seg.artifact?.size) {
						<span class="cs-chat__activity-secs">{{ seg.artifact?.size }}</span>
					}
				</a>
			} @else if (seg.kind === 'todos') {
				<!-- Live task checklist for a multi-step report; ticks off as the assistant works. -->
				<div class="cs-chat__todos" role="list">
					<div class="cs-chat__todos-head">
						Plan
						<span class="cs-chat__todos-count">{{ seg.todos?.doneCount }}/{{ seg.todos?.items?.length }}</span>
					</div>
					@for (item of seg.todos?.items ?? []; track $index) {
						<div class="cs-chat__todo" role="listitem" [class.is-done]="item.done">
							<span class="cs-chat__todo-box" aria-hidden="true">{{ item.done ? '✓' : '' }}</span>
							<span class="cs-chat__todo-text">{{ item.text }}</span>
						</div>
					}
				</div>
			} @else if (seg.kind === 'terminal') {
					<!-- Live/persisted console output of a code run (terminal block). -->
					<div class="cs-chat__terminal"
						 [class.is-running]="seg.terminal?.status === 'running'"
						 [class.is-success]="seg.terminal?.status === 'success'"
						 [class.is-error]="seg.terminal?.status === 'error'">
						<div class="cs-chat__terminal-bar">
							<!-- Status indicator: green=success, amber pulsing=busy, red=error. -->
							<span class="cs-chat__terminal-lights" aria-hidden="true">
								<span class="cs-chat__terminal-light cs-chat__terminal-light--red"></span>
								<span class="cs-chat__terminal-light cs-chat__terminal-light--yellow"></span>
								<span class="cs-chat__terminal-light cs-chat__terminal-light--green"></span>
							</span>
							<span class="cs-chat__terminal-title">python{{ seg.terminal?.status === 'running' ? ' · running…' : seg.terminal?.status === 'error' ? ' · error' : '' }}</span>
						</div>
						<!-- The source that ran. Collapsed by default: the output is the point, but without this a
						     cell that only imports or assigns is an unexplained empty box. Absent on transcripts
						     written before the fence carried code=. -->
						@if (seg.terminal?.code) {
							<details class="cs-chat__terminal-code">
								<summary class="cs-chat__terminal-code-summary">Code</summary>
								<pre class="cs-chat__terminal-code-body">{{ seg.terminal?.code }}</pre>
							</details>
						}
						@if (seg.terminal?.text?.trim()) {
							<pre class="cs-chat__terminal-body">{{ seg.terminal?.text }}</pre>
						} @else if (seg.terminal?.status !== 'running') {
							<!-- Say it produced nothing, rather than leave a blank box that reads as broken. -->
							<div class="cs-chat__terminal-empty">(no output)</div>
						}
					</div>
				} @else if (seg.kind === 'tool') {
				<!-- Inline tool chip: stays in the transcript at the position the call happened.
				     Expandable (native <details>) to reveal the call's input args + result preview. -->
				@if (seg.tool?.input || seg.tool?.result) {
					<details class="cs-chat__tool-chip cs-chat__tool-chip--expandable">
						<summary class="cs-chat__tool-summary">
							<span class="cs-chat__tool-check" aria-hidden="true">✓</span>
							{{ seg.tool?.label }}
							@if (seg.tool?.summary) {
								<span class="cs-chat__tool-summary-text">· {{ seg.tool?.summary }}</span>
							}
							<span class="cs-chat__activity-secs">{{ seg.tool?.seconds }}s</span>
							<span class="cs-chat__tool-caret" aria-hidden="true">▾</span>
						</summary>
						<div class="cs-chat__tool-meta">
							@if (seg.tool?.input) {
								<div class="cs-chat__tool-meta-label">Input</div>
								<pre class="cs-chat__tool-pre">{{ seg.tool?.input }}</pre>
							}
							@if (seg.tool?.result) {
								<div class="cs-chat__tool-meta-label">Result</div>
								<pre class="cs-chat__tool-pre">{{ seg.tool?.result }}</pre>
							}
						</div>
					</details>
				} @else {
					<div class="cs-chat__tool-chip">
						<span class="cs-chat__tool-check" aria-hidden="true">✓</span>
						{{ seg.tool?.label }}
						@if (seg.tool?.summary) {
							<span class="cs-chat__tool-summary-text">· {{ seg.tool?.summary }}</span>
						}
						<span class="cs-chat__activity-secs">{{ seg.tool?.seconds }}s</span>
					</div>
				}
			}
		}

		@if (message().streaming) {
			<!-- @for keyed on label so the elapsed timer restarts whenever the activity changes. -->
			@for (act of message().activity ? [message().activity!] : []; track act.label) {
				<div class="cs-chat__activity" aria-live="polite">
					<!-- The Cohelion mark as the live progress indicator: the S-curve breathes,
					     the dot blinks in counter-phase (same loader as the chart-pending state). -->
					<svg class="cs-chat__activity-logo" viewBox="0 0 40 63" aria-hidden="true">
						<path class="cs-chat__chart-pending-curve" fill="#007EFF"
							  d="M39.9,12.9c0,7-5.7,12.8-12.8,12.9c-3.5,0-6.3,1.3-8.2,4.3c-2.1,3.4-2.1,6.8,0.1,10.1c1.8,2.8,4.3,4.6,7.7,4.8 c0.8,0.1,1.6,0,2.4-0.1c2.9-0.4,5.5,0.1,7.7,2c2.8,2.5,3.8,5.7,2.7,9.3s-3.7,5.7-7.4,6.2c-4.6,0.6-8.8-2.4-9.8-6.9 c-0.5-2.3-1.5-4.3-3.3-5.8c-2.3-1.9-4.9-2.5-7.9-2c-2.5,0.3-4.9,0.1-7-1.3c-2-1.4-3.3-3.2-3.8-5.6c-1-4.5,1.7-9.1,6.1-10.4 c0.8-0.2,1.5-0.3,2.3-0.4c1.6-0.2,2.9-0.9,3.9-2c1.9-2,3-4.4,2.9-7.3c0-1.1-0.2-2.1-0.6-3.1c-3-7.7,1.8-16.1,9.9-17.4 C32-1,38.7,3.9,39.7,11.1C39.8,11.7,39.8,12.3,39.9,12.9z"/>
						<path class="cs-chat__chart-pending-dot" fill="currentColor"
							  d="M11.3,20.7c0,3.1-2.6,5.6-5.7,5.6S0,23.7,0,20.7C0,17.6,2.6,15,5.7,15S11.3,17.6,11.3,20.7z"/>
					</svg>
					<span class="cs-chat__activity-label">{{ act.label }}</span>
					<span class="cs-chat__activity-secs" csElapsedTime></span>
				</div>
			}
		} @else {
			@if (glossaryTerms().length) {
				<details class="cs-chat__glossary">
					<summary class="cs-chat__glossary-summary">
						<span class="cs-chat__glossary-icon" aria-hidden="true">📖</span>
						Terms in this answer ({{ glossaryTerms().length }})
					</summary>
					<dl class="cs-chat__glossary-list">
						@for (t of glossaryTerms(); track t.slug) {
							<dt class="cs-chat__glossary-term">{{ t.term }}</dt>
							<dd class="cs-chat__glossary-def">{{ t.short }}</dd>
						}
					</dl>
				</details>
			}

			<!-- One footer line: response stats on the left, hover actions on the right. ASSISTANT rows
			     only — a 'system' row is an error notice (see appendError), which has no stats to report
			     and nothing worth exporting; "⤓ PDF" under "the provider is overloaded" is just noise. -->
			@if (message().role === 'assistant') {
			<div class="cs-chat__footer">
				@if (statsSegment(); as seg) {
					<!-- Headline only (total time + model); the full breakdown — tokens, tok/s,
					     first token, tool time — lives in the hover tooltip to keep the line clean. -->
					<div class="cs-chat__stats" [attr.data-tip]="statsTooltip(seg.stats!)">
						<span class="cs-chat__stats-value">{{ seg.stats!.totalSeconds }}s</span>
						@if (showDiagnostics() && seg.stats!.models.length > 0) {
							<span class="cs-chat__stats-sep">·</span>
							<span class="cs-chat__stats-models">{{ seg.stats!.models.join(' → ') }}</span>
						}
					</div>
				} @else {
					<span></span>
				}
				<div class="cs-chat__actions">
					@if (glossaryTerms().length) {
						<button class="cs-chat__action" type="button" (click)="requestExplain()"
								title="Re-ask the assistant to explain this answer in plain language">
							💬 Explain simply
						</button>
					}
					<button class="cs-chat__action" type="button" (click)="downloadPdf()" title="Export this response as PDF">
						⤓ PDF
					</button>
				</div>
			</div>
			}
		}
	</div>
}

@if (lightboxSpec) {
	<div class="cs-chat__lightbox" (click)="closeLightbox()">
		<div class="cs-chat__lightbox-panel" (click)="$event.stopPropagation()">
			<button class="cs-chat__lightbox-close" type="button"
					(click)="closeLightbox()" aria-label="Close" title="Close">✕</button>
			@if (lightboxSpec.title) {
				<figcaption class="cs-chat__lightbox-title">{{ lightboxSpec.title }}</figcaption>
			}
			<cs-chart [type]="lightboxSpec.type"
					  [data]="lightboxSpec.data"
					  [height]="lightboxChartHeight()"
					  [options]="chartOptions"></cs-chart>
		</div>
	</div>
}
